import type {
  HandlersAttachmentResponse,
  HandlersCtpResponse,
  HandlersDeliverableResponse,
  HandlersPnrResponse,
  HandlersProjectSummaryResponse,
  HandlersRipResponse,
  HandlersTarResponse,
  HandlersTrResponse,
} from '../../../app/athenaApiGen';
import { parseRawFormData } from '../../../dashboard/components/formUtils';

// Maps an approval-queue row's objectType to the read-only route for its
// underlying form. Returns null for object types that don't have a
// dedicated form page (Deliverable / Project — the approver reviews those
// from the ViewPanel itself). TAR splits on workflowType because IMCS TAR
// (`/tar-imcs`) and the legacy TAR (`/tar`) are different routes.
export function getFormViewRoute(
  objectType: string | undefined,
  objectId: string | undefined,
  workflowType: string | undefined,
): string | null {
  if (!objectId) return null;
  switch (objectType) {
    case 'CTP':
      return `/ctp/${objectId}?mode=view`;
    case 'PNR':
      return `/pnr/${objectId}?mode=view`;
    case 'RIP':
      return `/rip/${objectId}?mode=view`;
    case 'TAR': {
      const isImcs = (workflowType ?? '').toLowerCase().includes('imcs');
      return `${isImcs ? '/tar-imcs' : '/tar'}/${objectId}?mode=view`;
    }
    case 'TR':
      return `/tr/${objectId}?mode=view`;
    default:
      return null;
  }
}

export interface ViewPanelDisplayData {
  title: string;
  description?: string;
  dueDate?: string;
  dateSubmitted?: string;
  location?: string;
  expectedApprovalEnd?: string;
  typeOrCategory?: string;
  attachments?: HandlersAttachmentResponse[];
  // Object-type-specific extras. Renderer decides whether to display.
  tar?: {
    travelerName?: string;
    totalEstimatedCost?: string;
    travelDestination?: string;
    tripStart?: string;
    tripEnd?: string;
    numberOfNights?: string;
    numberOfDays?: string;
    projectName?: string;
    clin?: string;
  };
}

export function normalizeForViewPanel(
  objectType: string,
  data: unknown,
  workflowType?: string,
): ViewPanelDisplayData | undefined {
  if (!data) return undefined;

  switch (objectType) {
    case 'Deliverable': {
      const d = data as HandlersDeliverableResponse;
      return {
        title: d.deliverableTitle ?? '',
        description: d.description,
        dueDate: d.dueDate,
        dateSubmitted: d.dateSubmitted,
        location: d.location?.name,
        expectedApprovalEnd: d.expectedApprovalEnd,
        typeOrCategory: d.deliverableType?.title,
        attachments: d.attachments,
      };
    }
    case 'Project': {
      const d = data as HandlersProjectSummaryResponse & { description?: string, attachments?: HandlersAttachmentResponse[] };
      return {
        title: d.title ?? '',
        description: d.description,
        dueDate: undefined,
        dateSubmitted: d.dateCreated,
        location: d.locationName,
        expectedApprovalEnd: undefined,
        typeOrCategory: d.projectCode,
        attachments: d.attachments,
      };
    }
    case 'CTP': {
      const d = data as HandlersCtpResponse & { description?: string; dateSubmitted?: string };
      return {
        title: d.ctpNumber ?? '',
        description: d.description,
        dueDate: d.dueDate,
        dateSubmitted: d.dateSubmitted,
        location: undefined,
        expectedApprovalEnd: undefined,
        typeOrCategory: undefined,
        attachments: d.attachments,
      };
    }
    case 'PNR': {
      const d = data as HandlersPnrResponse & { description?: string; dateSubmitted?: string };
      return {
        title: d.pnrNumber ?? '',
        description: d.description,
        dueDate: d.dueDate,
        dateSubmitted: d.dateSubmitted,
        location: undefined,
        expectedApprovalEnd: undefined,
        typeOrCategory: undefined,
        attachments: d.attachments,
      };
    }
    case 'RIP': {
      const d = data as HandlersRipResponse & { description?: string; dateSubmitted?: string };
      return {
        title: d.ripNumber ?? '',
        description: d.description,
        dueDate: d.dueDate,
        dateSubmitted: d.dateSubmitted,
        location: undefined,
        expectedApprovalEnd: undefined,
        typeOrCategory: undefined,
        attachments: d.attachments,
      };
    }
    case 'TAR': {
      // subject/purposeJustification/travelDate are sourced from rawFormData
      // at runtime — not declared on HandlersTarResponse after the codegen
      // regen. Preserve runtime behavior with a permissive cast.
      const d = data as HandlersTarResponse & {
        subject?: string;
        purposeJustification?: string;
        travelDate?: string;
        description?: string;
        dateSubmitted?: string;
        RawFormData?: string;
      };
      // IMCS TAR captures Duty Location and Travel Destination as freeform
      // text (no FK), so those values land inside `rawFormData` rather
      // than the top-level dutyLocation/eventLocation columns. Same story
      // for the trip remarks ("purposeJustification"), which is the
      // closest thing this form has to a description field. Parse the
      // raw blob and prefer its values when the structured columns are
      // empty.
      const raw = parseRawFormData(d.rawFormData ?? d.RawFormData);
      const location =
        d.eventLocation ||
        d.dutyLocation ||
        raw.eventLocation ||
        raw.dutyLocation ||
        undefined;
      const description =
        d.description || raw.purposeJustification || raw.description || undefined;
      // TAR Type: derive from the approval-queue workflowType — the raw
      // values ("IMCS TAR Without DPdM/Director" etc.) are too technical
      // for an approver glance, so collapse to the workflow family.
      const wf = workflowType ?? '';
      const typeOrCategory = wf.toLowerCase().includes('imcs')
        ? 'IMCS TAR'
        : wf || undefined;
      // TAR-specific detail fields for the approver's glance. Most live
      // in rawFormData because the IMCS TAR form stores its user-visible
      // labels there rather than in structured columns. Format the cost
      // as USD currency; leave dates as ISO strings for the renderer.
      const rawCost = raw.totalNTETripCost;
      const totalEstimatedCost =
        rawCost && !Number.isNaN(Number(rawCost))
          ? Number(rawCost).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            })
          : undefined;
      const tar = {
        travelerName: raw.fromContactId || undefined,
        totalEstimatedCost,
        travelDestination: raw.eventLocation || d.eventLocation || undefined,
        tripStart: d.eventStartDate || raw.eventStartDate || undefined,
        tripEnd: d.eventEndDate || raw.eventEndDate || undefined,
        numberOfNights:
          raw.numberOfNights != null && raw.numberOfNights !== ''
            ? String(raw.numberOfNights)
            : undefined,
        numberOfDays:
          raw.numberOfTripDays != null && raw.numberOfTripDays !== ''
            ? String(raw.numberOfTripDays)
            : undefined,
        projectName: raw.projectName || undefined,
        clin: raw.clin || undefined,
      };
      return {
        title: d.tarNumber ?? '',
        description,
        dueDate: d.dueDate,
        dateSubmitted: d.dateSubmitted,
        location,
        expectedApprovalEnd: undefined,
        typeOrCategory,
        attachments: d.attachments,
        tar,
      };
    }
    case 'TR': {
      const d = data as HandlersTrResponse & { description?: string; dateSubmitted?: string };
      return {
        title: d.trNumber ?? '',
        description: d.description,
        dueDate: d.dueDate,
        dateSubmitted: d.dateSubmitted,
        location: undefined,
        expectedApprovalEnd: undefined,
        typeOrCategory: undefined,
        attachments: d.attachments,
      };
    }
    default:
      return undefined;
  }
}
